import { fetchFromBusinessCentral } from "./businessCentralUtils";
import { formatAxiosError } from "./common";
import { sicfeApi } from "../services/apiClients";
import { getInvoiceByNo } from "../services/invoiceService";
import { getFinalDocumentNo } from "../services/invoiceSyncService";
import paraguay from "../config/paraguay";
import db from "../db/database";

/**
 * Recupera el sello SICFE de un documento.
 * Antes de buscar el sello, debe existir el número definitivo del documento.
 * Si no es posible recuperarlo → lanza error y NO busca el sello.
 */
export const getSicfeDocumentId = async (documentNo) => {
  // Obtenemos la factura local para consultar el id de ticket si se consulto previamente
  let selectedInvoice = await getInvoiceByNo(documentNo);

  // Si ya tiene el sello guardado localmente retornamos directamente
  if (selectedInvoice?.SicfeDocumentId) {
    return selectedInvoice.SicfeDocumentId;
  }

  // Si NO tiene número definitivo, lo buscamos en BC
  let finalDocumentNo = await getFinalDocumentNo(selectedInvoice);

  let logs = await fetchFromBusinessCentral(`LogFe?$filter=Process_Code eq '${finalDocumentNo}'`);

  // Si la factura no se encuentra en el log actual (vigente desde la implementación del TPV en Paraguay),
  // se realiza una segunda búsqueda en el log histórico que contiene las facturas generadas antes del 01/10/25.
  if (!logs || logs.length === 0) {
    logs = await fetchFromBusinessCentral(`LogFeTPV?$filter=Process_Code eq '${finalDocumentNo}'`);
  }

  // Si encontró registros, toma el Sello_Recibido
  if (logs?.length > 0 && logs[0]?.Sello_Recibido) {
    const sello = logs[0].Sello_Recibido;

    // Actualiza la factura en la base de datos local
    await db.invoices.update(selectedInvoice.id, {
      SicfeDocumentId: sello,
    });

    return sello;
  }

  return null;
};

export const fetchSicfeDocument = async (documentoId, ticket = true) => {
  try {
    const tipoDoc = !ticket ? 'A4' :
      paraguay.env == 'prod' ? 'FacturaTicketSantillanaprod' : 'FacturaTicketSantillanaTest';

    const url = `/emision/documentos/${documentoId}/kude?idImpresion=${tipoDoc}`;

    const response = await sicfeApi.get(url, { responseType: "blob" });

    if (response.status == 200) {
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl);
    }
    else {
      let descripcion = response.data?.descripcion || "Error al obtener la factura";
      window.messageApi.error(descripcion);
    }

    return true;
  } catch (error) {
    let message = "";

    // Si viene un Blob (cuando el backend devuelve error en PDF o blob genérico)
    if (error?.response?.data instanceof Blob) {
      const blob = error.response.data;

      try {
        const text = await blob.text();   // convertir Blob → texto
        const json = JSON.parse(text);    // intentar parsear → JSON

        message = json?.mensaje ||
          json?.descripcion ||
          "Error al llamar a Sicfe para obtener la factura. Intente nuevamente.";
      } catch (e) {
        message = "Error de formato de respuesta Sicfe."; //El error no vino estructurado como en la doc;
      }

    } else {
      message = formatAxiosError(error, `Error al llamar a Sicfe para obtener la factura. Intente nuevamente.`);
    }

    window.messageApi.error(message, { modal: true });
  }
};

//Obtiene el systemId para poder contabilizar la factura/nota de credito
export const getSystemId = async (invoice) => {
  const isCreditMemo = invoice.Document_Type === 'Credit Memo';
  let filter = `$filter=Sell_to_Customer_No eq '${invoice.Sell_to_Customer_No_}' and No eq '${invoice.No}' and Posting_Date eq ${invoice.Posting_Date}`;

  let results = isCreditMemo ?
    await fetchFromBusinessCentral(`SalesCreditMemo?${filter}`) :
    await fetchFromBusinessCentral(`SalesInvoice?${filter}`);

  if (!results || results.length === 0) {
    throw new Error(
      `No fue posible recuperar el documento para obtener el systemId necesario para contabilizar.`
    );
  }

  let systemId = results[0].SystemId;

  await db.invoices.update(invoice.id, {
    ...invoice,
    systemId,
  });

  return systemId;
};
